import { useEffect, useMemo, useState } from 'react';

import { IoCheckmarkCircle, IoChevronDown } from 'react-icons/io5';

import { translate } from '@/i18n';
import { useAssetHooks } from '@/shared/api/assets/assets.hooks.ts';
import { useUpdateCraneLoadedStatus } from '@/shared/hooks/assets/useUpdateCraneLoadedStatus.ts';
import { notifyService } from '@/shared/services/notify.service.tsx';
import { getCraneLoadedStatus, isCraneAsset } from '@/shared/utils/crane-utils.ts';

import { AssetBody } from '@/shared/types/assets/assets.types.ts';

export const CraneLoadUnload = () => {
  const equipments = useAssetHooks.getAllEquipments();
  const updateCraneLoadedStatus = useUpdateCraneLoadedStatus();
  const [selectedCrane, setSelectedCrane] = useState<AssetBody | null>(null);

  // Filter crane assets - memoized to prevent unnecessary re-renders
  const craneAssets = useMemo(
    () => equipments.data?.filter(asset => isCraneAsset(asset)) ?? [],
    [equipments.data],
  );

  // Auto-select first crane and handle stale references
  // This effect ensures:
  // 1. First crane is auto-selected when list becomes available
  // 2. Selected crane is updated with fresh data after mutations/refetches
  // 3. Selection is cleared/reset if the selected crane is removed from the list
  useEffect(() => {
    if (craneAssets.length === 0) {
      // No cranes available, clear selection
      setSelectedCrane(null);
      return;
    }

    if (!selectedCrane) {
      // No crane selected, select the first one
      setSelectedCrane(craneAssets[0]);
      return;
    }

    // Check if the currently selected crane still exists in the list
    const updatedCrane = craneAssets.find(
      crane => crane.assetResName === selectedCrane.assetResName,
    );

    if (!updatedCrane) {
      // Selected crane no longer exists, select the first available crane
      setSelectedCrane(craneAssets[0]);
      return;
    }

    // Update selected crane with latest data (handles mutations/refetches)
    // Note: setSelectedCrane will only trigger a re-render if the reference changes,
    // and since craneAssets is memoized, this only happens when data actually changes
    setSelectedCrane(updatedCrane);
  }, [craneAssets, selectedCrane]);

  const handleStatusChange = (loaded: boolean) => {
    if (!selectedCrane) {
      notifyService.error(translate('cranes.selectCraneFirst'));
      return;
    }

    // Trigger mutation in background - UI updates optimistically via onMutate
    // Error handling is done in the mutation hook's onError callback
    updateCraneLoadedStatus.mutate({
      assetResName: selectedCrane.assetResName,
      loaded,
    });
  };

  if (equipments.isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="flex flex-col items-center justify-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
          <p className="text-lg font-medium text-slate-600">{translate('cranes.loading')}</p>
        </div>
      </div>
    );
  }

  if (craneAssets.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
          <h2 className="mb-2 text-center text-xl font-semibold">
            {translate('cranes.noCranesAvailable')}
          </h2>
          <p className="text-center text-slate-600">{translate('cranes.noCranesDescription')}</p>
        </div>
      </div>
    );
  }

  const loadedStatus = selectedCrane ? getCraneLoadedStatus(selectedCrane) : undefined;
  const isLoaded = loadedStatus === true;
  const isUnloaded = loadedStatus === false;

  return (
    <div
      className="mobile-landscape:!p-4 flex h-full flex-col overflow-hidden p-4 md:p-6 mobile-landscape:overflow-auto"
    >
      {/* Header with dropdown */}
      <div className="mobile-landscape:!mb-0 relative mb-0 flex w-full items-center justify-center md:mb-2 md:justify-center">
        <p className="mobile-landscape:!hidden hidden flex-1 text-center text-[24px] font-medium tracking-[0.05px] md:block md:text-[48px]">
          {translate('cranes.status')}
        </p>

        {/* Crane selector dropdown */}
        <div className="mobile-landscape:!relative mobile-landscape:!w-full relative w-full md:absolute md:right-0 md:w-auto">
          <select
            value={selectedCrane?.assetResName ?? ''}
            onChange={event => {
              const crane = craneAssets.find(crane => crane.assetResName === event.target.value);
              setSelectedCrane(crane ?? null);
            }}
            style={{
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              appearance: 'none',
              backgroundImage: 'none',
            }}
            className="crane-select mobile-landscape:!h-[40px] mobile-landscape:!w-full mobile-landscape:!text-[16px] mobile-landscape:!transition-none h-[40px] w-full appearance-none rounded-lg border border-slate-300 bg-white pl-4 pr-12 text-[16px] font-normal shadow-sm transition-colors hover:border-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 md:h-[46px] md:w-[232px] md:text-[20px]"
          >
            {craneAssets.map(crane => (
              <option key={crane.assetResName} value={crane.assetResName}>
                {crane.assetName}
              </option>
            ))}
          </select>
          <IoChevronDown className="mobile-landscape:!size-4 pointer-events-none absolute right-4 top-1/2 z-10 size-4 -translate-y-1/2 text-slate-500 md:size-5" />
        </div>
      </div>

      <div className="mobile-landscape:!flex-row mobile-landscape:!gap-2 mt-2 flex flex-1 flex-col gap-2 md:mt-2 md:flex-row md:gap-8">
        {/* Loaded Button */}
        <button
          type="button"
          onClick={() => handleStatusChange(true)}
          className={`mobile-landscape:!border-[8px] mobile-landscape:!transition-none mobile-landscape:!scale-100 mobile-landscape:hover:!scale-100 relative flex flex-1 items-center justify-center rounded-[6px] border-[8px] shadow-lg transition-all duration-200 hover:scale-105 md:border-[12px] ${
            isLoaded
              ? 'border-[#263289] bg-blue-600 text-white'
              : 'border-[#E2E8F0] bg-[#E2E8F0] text-slate-700 hover:border-[#E2E8F0]'
          }`}
        >
          <span
            className={`mobile-landscape:!text-[24px] text-[24px] font-medium leading-none md:text-[32px] lg:text-[48px] ${isLoaded ? 'text-white' : 'text-slate-800'}`}
          >
            {translate('cranes.loaded')}
          </span>
          <IoCheckmarkCircle
            className={`mobile-landscape:!mt-4 mobile-landscape:!size-[32px] mobile-landscape:!transition-none mobile-landscape:!scale-100 absolute left-1/2 top-1/2 mt-4 size-[32px] -translate-x-1/2 text-white drop-shadow-md transition-all duration-200 md:mt-6 md:size-[44px] lg:mt-10 lg:size-[56px] ${
              isLoaded ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
            }`}
          />
        </button>

        {/* Unloaded Button */}
        <button
          type="button"
          onClick={() => handleStatusChange(false)}
          className={`mobile-landscape:!border-[8px] mobile-landscape:!transition-none mobile-landscape:!scale-100 mobile-landscape:hover:!scale-100 relative flex flex-1 items-center justify-center rounded-[6px] border-[8px] shadow-lg transition-all duration-200 hover:scale-105 md:border-[12px] ${
            isUnloaded
              ? 'border-[#263289] bg-blue-600 text-white'
              : 'border-[#E2E8F0] bg-[#E2E8F0] text-slate-700 hover:border-[#E2E8F0]'
          }`}
        >
          <span
            className={`mobile-landscape:!text-[24px] text-[24px] font-medium leading-none md:text-[32px] lg:text-[48px] ${isUnloaded ? 'text-white' : 'text-slate-800'}`}
          >
            {translate('cranes.unloaded')}
          </span>
          <IoCheckmarkCircle
            className={`mobile-landscape:!mt-4 mobile-landscape:!size-[32px] mobile-landscape:!transition-none mobile-landscape:!scale-100 absolute left-1/2 top-1/2 mt-4 size-[32px] -translate-x-1/2 text-white drop-shadow-md transition-all duration-200 md:mt-6 md:size-[44px] lg:mt-10 lg:size-[56px] ${
              isUnloaded ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
            }`}
          />
        </button>
      </div>
    </div>
  );
};
